import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { Section, Band, Arrow } from "../components/Section";
import { CompareTable } from "../components/Table";
import { Box, Users, BookOpen } from "lucide-react";
import { EditorialIcon, ArchitectureIllustration } from "../components/EditorialVisuals";

const PATHS = [
  {
    label: "Technology",
    icon: Box,
    title: "Aky, AI in a Box",
    body: "Technology that confines AI in a box you control. It cannot do, touch or see what you have not permitted. Early access November 2026.",
    to: "/aky",
    link: "Aky",
  },
  {
    label: "Capacity",
    icon: Users,
    title: "Lead.ai",
    body: "Programmes and a Fellowship with the Aspen Institute that equip executives and boards to lead AI adoption and govern it.",
    to: "/lead-ai",
    link: "Lead.ai",
  },
  {
    label: "Research",
    icon: BookOpen,
    title: "Research",
    body: "What we are learning as we build and as enterprises adopt, published as we learn.",
    to: "/research",
    link: "Research",
  },
];

export default function Home() {
  return (
    <>
      <Seo
        title="Syni.ai — Regulation will come. Insulation happens now."
        description="Syni.ai builds the technology, the executive capacity and the research that let enterprises in Africa and other emerging markets keep control of AI as they scale it."
        path="/"
      />

      <section className="hero home-illustrated-hero" data-testid="home-hero">
        <div className="container-syni">
          <h1 className="h-display hero-headline" data-testid="home-hero-title">
            Regulation will come.
            <br />
            Insulation happens now.
          </h1>
          <p className="lead hero-sub" data-testid="home-hero-lead">
            Syni.ai builds the technology, the executive capacity and the research that let enterprises in Africa and other emerging markets
            keep control of AI as they scale it.
          </p>
        </div>
        <ArchitectureIllustration kind="insulation" className="home-hero-art" testId="home-insulation-illustration" priority />
      </section>

      <Section
        n="01"
        label="The argument"
        title="Governing AI needs more than regulation."
        split
        pale
        testId="home-argument"
        below={<CompareTable testId="home-compare-table" />}
      >
        <div className="prose body-copy measure">
          <p>
            There is broad agreement on what AI can do for health, science, productivity and access. There is no agreement on the questions
            that matter once it is deployed: who controls it when it acts on its own, whose data built it and who is paid, who carries the
            loss when it is wrong.
          </p>
          <p>
            Regulation will answer some of these, years from now, written by those with the capital to shape it. Enterprises cannot wait. The
            controls that matter today are the ones you own.
          </p>
        </div>
      </Section>

      <Section n="02" label="Three paths" testId="home-paths">
        <div className="grid-cards grid-cards--3" data-testid="home-path-cards">
          {PATHS.map((p) => (
            <Link key={p.to} to={p.to} className="card card--link" data-testid={`path-card-${p.link.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              <div className="card-heading-row">
                <span className="mono-label" data-testid={`home-path-${p.label.toLowerCase()}-label`}>{p.label}</span>
                <EditorialIcon icon={p.icon} testId={`home-path-${p.label.toLowerCase()}-icon`} />
              </div>
              <h3 className="card-title">{p.title}</h3>
              <p className="card-body">{p.body}</p>
              <span className="card-foot link-arrow">
                <Arrow /> {p.link}
              </span>
            </Link>
          ))}
        </div>
      </Section>

      <Section
        n="03"
        label="For Africa"
        title="Regulation is written by those who can afford to shape AI. Insulation is built by those who have to live with it."
        split
        pale
        testId="home-africa"
      >
        <p className="body-copy measure">
          For Africa and other emerging markets, insulation will play the bigger role. It keeps what is created here owned here, contextualises
          AI for local use without the baggage of the AI race, puts controls in place before rollout rather than after, and leaves the
          redefinition of work to be decided locally. It is the lever within reach.
        </p>
      </Section>

      <Band
        label="Syni.ai · Nairobi"
        body="A pan-African applied AI research and product studio helping enterprises adopt and scale AI safely."
        links={[
          { to: "/about", label: "About", testId: "home-close-about" },
          { to: "/contact", label: "Contact", testId: "home-close-contact" },
        ]}
        testId="home-close"
      />
    </>
  );
}
